import unfetch from 'isomorphic-unfetch';
import qs from 'qs';
import { Request } from 'express';
import { getRequestDataFromRequest } from './RequestContext';
import { NextApiRequest } from 'next';

export enum FilterSort {
  Popularity = 'popularity',
  Rating = 'weightedRating',
  Date = 'dateCreated',
  Title = 'title',
  Relevance = 'relevance',
}

export enum FilterContentType {
  Worksheets = 'worksheets',
  Games = 'games',
  GuidedLessons = 'guided-lessons',
  LessonPlans = 'lesson-plans',
  Activities = 'activity',
  Stories = 'stories',
  Exercises = 'exercises',
  Workbooks = 'workbooks',
  ScienceProjects = 'science-fair',
  Songs = 'songs',
}

export interface SearchFilter {
  contentType: FilterContentType | null;
  el: string | null;
  grade: string | null;
  page: number;
  q: string;
  resultsPerPage: number;
  sort: FilterSort;
  standard: string | null;
  subject: string | null;
  topic: string | null;
}

export interface AppliedFilter {
  display: string;
  group: string;
  href: string;
}

export interface SearchFacet {
  display: string;
  internalName: string;
  hasChildren: boolean;
  children?: SearchFacet[];
}

export interface SearchMeta {
  bottomDescription: string;
  bottomTitle: string;
  contextualLinks: string[];
  contextualLinksTitle: string;
  dateUpdated: string;
  definition: string;
  definitionTitle: string;
  description: string;
  index: boolean;
  learnosityActivityUrl: string;
  metaDescription: string;
  metaTitle: string;
  showDefinition: boolean;
  title: string;
  url: string;
}

export interface ThumbnailItem {
  src: string;
  height: number;
  width: number;
}

interface ContentResult {
  type: string;
  id: string;
  title: string;
  description: string;
  url: string;
  thumbnail: Thumbnail;
  category: string;
  rating: number;
  isNew: boolean;
  answerSheet: boolean;
  el: boolean;
  meta: SearchMeta;
  isInteractive: boolean;
  published: boolean;
}

export interface ContentResultV1 {
  answersheet: boolean;
  description: string;
  el: boolean;
  grade: string;
  id: string;
  internalName: string;
  new: boolean;
  premium: boolean;
  rating: number;
  subject: string;
  thumbnail: ThumbnailItem;
  title: string;
  type: string;
  url: string;
}

export interface SearchResponse {
  total: number;
  pages: number;
  urlPath: string;
  meta: SearchMeta;
  selectedCategory: TaxonomyCategory;
  selectedFilters: TaxonomyFilter[];
  results: ContentResult[];
  suggestions: string[];
  viableSubjectCategories: ViableCategory[];
  viableCategories: ViableCategory | ViableCategory[];
  viableFilters: ViableFilter[];
  viableGrades: GradeFilter;
  viableContentTypes: ViableFilter;
  params: SearchParams;
  redirect: string;
}

export interface SearchResponseV1 {
  urlPath: string;
  total: number;
  pages: number;
  meta: SearchMeta;
  filter: SearchFilter;
  facets: SearchFacet[];
  currentFilters: AppliedFilter[];
  results: ContentResultV1[];
  suggestions: string[];
}

interface Thumbnail {
  width: number;
  height: number;
  src: string;
}

interface TaxonomyCategory {
  children: TaxonomyCategory[];
  urlSlice: string;
  text: string;
  visible: boolean;
  shortCategoryName: string;
  path: string;
}

interface TaxonomyFilter {
  name: string;
  url: string;
  attributes: TaxonomyFilterAttribute[];
}

interface ViableCategory {
  urlSlice: string;
  count: number;
  text: string;
  href: string;
  children: ViableCategory[];
}

interface TaxonomyFilter {
  name: string;
  url: string;
  attributes: TaxonomyFilterAttribute[];
}

interface TaxonomyFilterAttribute {
  name: string;
  url: string;
}

interface GradeFilter {
  name: string;
  href: string;
  anyContent: boolean;
  attributes: GradeFilterAttribute[];
  filterRank: number;
}

interface GradeFilterAttribute {
  name: string;
  urlSlice: string;
  count: number;
  selected: boolean;
  href: string;
  shortName: string;
  id: number;
}

interface ViableFilter {
  name: string;
  urlSlice: string;
  anyContent: boolean;
  attributes: ViableAttribute[];
  filterRank: number;
}

interface ViableAttribute {
  name: string;
  urlSlice: string;
  count: number;
  selected: boolean;
  href: string;
}

interface SearchParams {
  page: number;
  sort: string;
  q: string;
}

export interface ContentStandard {
  internalName: string;
  urlName: string;
  name: string;
  weight: number;
  type: string;
}

export interface ContentGrade {
  internalName: string;
  urlName: string;
  name: string;
  weight: number;
}

export interface ContentSubject {
  internalName: string;
  urlName: string;
  name: string;
  weight: number;
}

export interface ContentSlideshow {
  url: string;
  title: string;
  count: number;
}

export interface ContentCount {
  games: number;
  exercises: number;
  songs: number;
  stories: number;
  worksheets: number;
}

export interface RelatedGuidedLesson {
  title: string;
  urlName: string;
  images: string[];
  contentCounts: ContentCount;
  lessonExercises: string[];
  grade: string;
  subject: string;
  skill: string;
}

export interface GuidedLessonCourse {
  internalName: string;
  subject: string;
}

export interface Worksheet {
  id: string;
  urlName: string;
  title: string;
  metaTitle: string;
  description: string;
  metaDescription: string;
  createdAt: string;
  updatedAt: string;
  published: boolean;
  indexed: boolean;
  isInteractive: boolean;
  text: string;
  taxonomyCategories: TaxonomyContentCategory[];
  grades: TaxonomyContentGrade[];
  standards: ContentStandard[];
  mainImage: string;
  slideshow: Slideshow;
  lessonPlan: string;
  downloadHref: string;
  answersheetHref: string;
}

export interface Game {
  id: string;
  urlName: string;
  internalName: string;
  title: string;
  metaTitle: string;
  description: string;
  metaDescription: string;
  createdAt: string;
  updatedAt: string;
  published: boolean;
  indexed: boolean;
  text: string;
  taxonomyCategories: TaxonomyContentCategory[];
  grades: TaxonomyContentGrade[];
  standards: ContentStandard[];
  mainImage: string;
  allowAnonPlay: boolean;
  type: string;
}

export interface LessonPlan {
  id: string;
  urlName: string;
  internalName: string;
  type: string;
  isPreLesson: boolean;
  isSubPacket: boolean;
  title: string;
  metaTitle: string;
  description: string;
  metaDescription: string;
  createdAt: string;
  updatedAt: string;
  published: boolean;
  indexed: boolean;
  meta: SearchMeta;
  linkedLesson: LinkedLesson[];
  taxonomyCategories: TaxonomyContentCategory[];
  grades: TaxonomyContentGrade[];
  standards: ContentStandard[];
  mainImage: string;
}

interface LinkedLesson {
  urlName: string;
  internalName: string;
  type: string;
  title: string;
}

interface Slideshow {
  id: number;
  url: string;
  title: string;
  count: number;
}

interface TaxonomyContentCategory {
  text: string;
  urlSlice: string;
  visible: boolean;
  orderInCategory: number;
}

interface TaxonomyContentGrade {
  name: string;
  urlSlice: string;
}

export interface ContentItem {
  id: string;
  urlName: string;
  title: string;
  metaTitle: string;
  description: string;
  metaDescription: string;
  createdAt: string;
  updatedAt: string;
  indexed: boolean;
  published: boolean;
  text: string;
  standards: ContentStandard[];
  grades: ContentGrade[];
  subjects: ContentSubject[];
  mainImage: string;
}

export interface WorksheetV1 extends ContentItem {
  slideshow: ContentSlideshow | null;
  lessonPlan: any; // TODO: Confirm data type for worksheet.lessonPlan
  guidedLesson: RelatedGuidedLesson | null;
  downloadHref: string;
  answersheetHref: string | null;
  isInteractive: boolean;
}

export interface GameV1 extends ContentItem {
  guidedLesson: RelatedGuidedLesson | null;
  course: GuidedLessonCourse;
  allowAnonPlay: boolean;
  type: string;
}

export interface LessonPlanV1 extends ContentItem {
  allowAnonPlay: boolean;
  type: string;
}

export interface RelatedMaterialResponse {
  related: ContentResultV1[];
}

export interface DirectShareData {
  id: string;
  token?: number;
  worksheetId?: string;
  memberId?: number;
  isPremium?: boolean;
}

export interface Player {
  playerId: number;
  playerName: string;
  playerAvatarId: number;
  directShare: string;
}
export interface ResourceItem {
  title: string;
  type: string;
  column: number;
  url: string;
  icon?: string | null;
}

export interface Section {
  title: string;
  items: ResourceItem[];
}

export interface SeeAll {
  title: string;
  column: number;
  url: string;
  icon: string | null;
}

export interface NavigationInfo {
  _id: string;
  name: string;
  order: number;
  urlName: string;
  sections: Section[];
  hasDropdown: boolean;
}

export interface NavigationInfoV1 {
  _id: string;
  name: string;
  order: number;
  urlName: string;
  seeAll?: SeeAll;
  sections: Section[];
  gradesForContentType: {
    urlName: string;
    longDisplay: string;
  };
  hasDropdown: boolean;
}

export interface ContentApi {
  search(path: string, onlyValidUrls?: boolean): Promise<SearchResponse>;
  searchV1(path: string, onlyValidUrls?: boolean): Promise<SearchResponseV1>;
  recordView(type: string, id: string): Promise<void>;
  getWorksheetDetails(urlName: string): Promise<Worksheet>;
  getGameDetails(urlName: string): Promise<Game>;
  getLessonPlanDetails(urlName: string): Promise<LessonPlan>;
  getWorksheetDetailsV1(urlName: string): Promise<WorksheetV1>;
  getGameDetailsV1(urlName: string): Promise<GameV1>;
  getLessonPlanDetailsV1(urlName: string): Promise<LessonPlanV1>;
  getRelatedMaterials(
    type: string,
    id: string
  ): Promise<RelatedMaterialResponse>;
  getDirectShareDataByToken(
    token: number,
    serverSideReq?: NextApiRequest
  ): Promise<DirectShareData>;
  getDirectShareDataByMemberAndWorksheetId(
    memberId: number,
    worksheetId: string
  ): Promise<DirectShareData>;
  verifyAccessByStudentCode(
    token: number,
    studentCode: string
  ): Promise<Player>;
  getNavigationInfo(): Promise<NavigationInfo[]>;
  getNavigationInfoV1(): Promise<NavigationInfoV1[]>;
}

/**
 * Replace the query params in a URL.
 */
export function replaceParams(
  url: string,
  params?: Record<string, string>
): string {
  const [path = '', query = ''] = url.split('?');
  const parsed = qs.parse(query, { ignoreQueryPrefix: true, decoder: s => s });
  const newParams = Object.assign(parsed, params);
  Object.keys(newParams).forEach(
    key => !newParams[key] && delete newParams[key]
  );
  if (!Object.keys(newParams).length) return path;

  return path + '?' + qs.stringify(newParams, { encode: false });
}

export default (req: Request): ContentApi => {
  const requestData = getRequestDataFromRequest(req);
  const CONTENT_SERVICE_HOST = requestData.contentServiceHost;

  const request = async <T>(url: string, options?: RequestInit): Promise<T> => {
    const response = await unfetch(CONTENT_SERVICE_HOST + url, {
      mode: 'cors',
      cache: 'default',
      headers: {
        'Content-Type': 'application/json',
      },
      ...options,
    });

    if (!response.ok) {
      // Retrieve response as a string
      const message = await response.text();
      let errorObj;

      try {
        // Attempt to parse string as JSON; if it fails pass as string
        const jsonMessage = JSON.parse(message);
        errorObj = { statusCode: response.status, ...jsonMessage };
      } catch (error) {
        if (response.status == 502) {
          // EDENG-1177: Cloudflare returns an HTML response in the case of a bad gateway; continue to log but handle
          // gracefully
          errorObj = {
            statusCode: response.status,
            message: '502: Bad gateway response from Content Service',
          };
        } else {
          // Otherwise, return the full text string as the message for debugging
          errorObj = { statusCode: response.status, message: message };
        }
      }

      throw errorObj;
    }

    return response.json();
  };

  /**
   * Gets the worksheet details.
   * @param urlName The worksheet urlName
   */
  const getWorksheetDetailsV1: ContentApi['getWorksheetDetailsV1'] = async function(
    urlName
  ) {
    return request<WorksheetV1>(`/worksheet/${urlName}`, {
      method: 'GET',
    });
  };

  /**
   * Gets the game details.
   * @param urlName The game urlName
   */
  const getGameDetailsV1: ContentApi['getGameDetailsV1'] = async function(
    urlName
  ) {
    return request(`/game/${urlName}`, {
      method: 'GET',
    });
  };

  /**
   * Gets the lesson-plan details.
   * @param urlName The lesson-plan urlName
   */
  const getLessonPlanDetailsV1: ContentApi['getLessonPlanDetailsV1'] = async function(
    urlName
  ) {
    return request(`/lessonPlan/${urlName}`, {
      method: 'GET',
    });
  };

  /**
   * Gets the worksheet details.
   * @param urlName The worksheet urlName
   */
  const getWorksheetDetails: ContentApi['getWorksheetDetails'] = async function(
    urlName
  ) {
    return request<Worksheet>(`/v2/worksheet/${urlName}`, {
      method: 'GET',
    });
  };

  /**
   * Gets the game details.
   * @param urlName The game urlName
   */
  const getGameDetails: ContentApi['getGameDetails'] = async function(urlName) {
    return request(`/v2/game/${urlName}`, {
      method: 'GET',
    });
  };

  /**
   * Gets the lesson-plan details.
   * @param urlName The lesson-plan urlName
   */
  const getLessonPlanDetails: ContentApi['getLessonPlanDetails'] = async function(
    urlName
  ) {
    return request(`/v2/lessonPlan/${urlName}`, {
      method: 'GET',
    });
  };

  /**
   * Gets related materials for a piece of content.
   */
  const getRelatedMaterials: ContentApi['getRelatedMaterials'] = async function(
    type,
    id
  ) {
    return request<RelatedMaterialResponse>(
      `/recommendations/related/${type}/${id}`,
      {
        method: 'GET',
      }
    );
  };

  /**
   * Records a content view.
   */
  const recordView: ContentApi['recordView'] = async function(type, id) {
    try {
      await request('/popularity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          id,
        }),
      });
    } catch (e) {
      // This error isn't fatal, just log and move on
      console.error(e);
    }
  };

  /**
   * Search the content API.
   * @param path The search results url path.
   * @param onlyValidUrls If true, don't return results if the path is malformed but parsable.
   */
  const search: ContentApi['search'] = async function(
    path,
    onlyValidUrls = false
  ) {
    return request<SearchResponse>('/v2/search', {
      method: 'POST',
      body: JSON.stringify({ path: path, onlyValidUrls: onlyValidUrls }),
    });
  };

  /**
   * Search the content API.
   * @param path The search results url path.
   * @param onlyValidUrls If true, don't return results if the path is malformed but parsable.
   */
  const searchV1: ContentApi['searchV1'] = async function(
    path,
    onlyValidUrls = false
  ) {
    const query = qs.stringify({
      path,
      onlyValidUrls,
    });

    return request<SearchResponseV1>('/search?' + query, {
      method: 'GET',
    });
  };

  const getDirectShareDataByToken: ContentApi['getDirectShareDataByToken'] = async function(
    token
  ) {
    return request(
      `/interactive-worksheet/get-direct-share-by-token/${token}`,
      {
        method: 'GET',
      }
    );
  };

  /**
   * Pulls an existing token for a memberId + worksheetId pair if there is one
   */
  const getDirectShareDataByMemberAndWorksheetId: ContentApi['getDirectShareDataByMemberAndWorksheetId'] = async function(
    memberId,
    worksheetId
  ) {
    return request(
      `/interactive-worksheet/get-direct-share/${memberId}/${worksheetId}`,
      {
        method: 'GET',
      }
    );
  };

  /**
   * Verify student access by student code for direct-link sharing
   */
  const verifyAccessByStudentCode: ContentApi['verifyAccessByStudentCode'] = async function(
    token,
    studentCode
  ) {
    return request(
      `/interactive-worksheet/get-player/${token}/${studentCode}`,
      {
        method: 'GET',
      }
    );
  };

  /**
   * Gets the top nav info from mongo (Navigation -> topNavLinks)
   */
  const getNavigationInfoV1: ContentApi['getNavigationInfoV1'] = async function() {
    return request(`/navigation/top-nav-links`, {
      method: 'GET',
    });
  };

  const getNavigationInfo: ContentApi['getNavigationInfo'] = async function() {
    return request(`/v2/navigation/top-nav-links`, {
      method: 'GET',
    });
  };

  return {
    search,
    searchV1,
    recordView,
    getWorksheetDetails,
    getGameDetails,
    getLessonPlanDetails,
    getWorksheetDetailsV1,
    getGameDetailsV1,
    getLessonPlanDetailsV1,
    getRelatedMaterials,
    getDirectShareDataByToken,
    getDirectShareDataByMemberAndWorksheetId,
    verifyAccessByStudentCode,
    getNavigationInfo,
    getNavigationInfoV1,
  };
};
